"use client";

import { ListSkeleton } from "@/components/ui/skeleton";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { CheckIcon, CopyIcon, DownloadIcon, EyeOffIcon, MessageSquareIcon, RefreshCcwIcon, SearchIcon, SparklesIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// ─── Types ────────────────────────────────────────────────────────────────────

type FeedbackStatus = "new" | "investigating" | "completed";
type FeedbackTargetKind = "chat" | "project-chat";

type FeedbackItem = {
  id: string;
  chat_id: string | null;
  project_chat_id: string | null;
  user_id: string;
  feedback: string;
  consent_to_read_chat: boolean;
  created_at: string;
  status: FeedbackStatus;
  title: string | null;
  submitter_name: string | null;
};

type AdminFeedbackListItem = {
  id: string;
  title: string | null;
  target_type: "chat" | "project-chat" | "meeting-recording" | "general";
  target_id: string | null;
  feedback: string;
  consent_to_read_chat: boolean;
  status: FeedbackStatus;
  created_at: string;
  created_by: { user_id: string; name: string | null };
};

const STATUS_FILTERS: { value: FeedbackStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "investigating", label: "Investigating" },
  { value: "completed", label: "Completed" },
];

type MessagePart = { type: string; text?: string; [key: string]: unknown };

type AdminMessage = {
  id: string;
  content: string;
  parts: MessagePart[] | null;
  role: string;
  model: string;
  sequence_number: number;
  created_at: string;
  agent_id: string | null;
  image_paths: string[];
};

type ChatDetails = {
  id: string;
  name: string;
  created_at: string;
  model: string;
  agent_id: string | null;
  agent_name: string | null;
  active_tool_names: string[];
};

type FeedbackInfo = {
  user_id: string;
  submitter_name: string | null;
  feedback: string;
  consent_to_read_chat: boolean;
  created_at: string;
};

type AdminChatSuccessResponse = {
  success: true;
  chat_id: string;
  target_type: FeedbackTargetKind;
  chat_feedback: FeedbackInfo;
  chat_details: ChatDetails | null;
  messages: AdminMessage[];
  message_count: number;
  access_limited?: boolean;
};

type AdminChatErrorResponse = { error: string };
type AdminChatApiResponse = AdminChatSuccessResponse | AdminChatErrorResponse;

type SelectedChat = {
  chatId: string;
  targetKind: FeedbackTargetKind | null;
  feedbackId: string | null;
  feedbackStatus: FeedbackStatus;
  feedbackInfo: FeedbackInfo | null;
  chatDetails: ChatDetails | null;
  messages: AdminMessage[];
  accessLimited: boolean;
  loading: boolean;
  error: string | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFeedbackDate(value: string) {
  return new Intl.DateTimeFormat("en-NZ", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Pacific/Auckland",
  }).format(new Date(value));
}

function getFeedbackTargetId(feedback: Pick<FeedbackItem, "chat_id" | "project_chat_id">): string | null {
  return feedback.project_chat_id ?? feedback.chat_id;
}

function getFeedbackTargetKind(
  feedback: Pick<FeedbackItem, "project_chat_id">
): FeedbackTargetKind {
  return feedback.project_chat_id ? "project-chat" : "chat";
}


function StatusBadge({ status }: { status: FeedbackStatus }) {
  if (status === "completed") {
    return (
      <Badge
        variant="secondary"
        className="shrink-0 bg-activated text-activated-foreground px-1.5 py-0 text-[10px]"
      >
        Completed
      </Badge>
    );
  }
  if (status === "investigating") {
    return (
      <Badge
        variant="outline"
        className="shrink-0 px-1.5 py-0 text-[10px] text-accent-foreground border-accent-foreground/30 bg-accent/40"
      >
        Investigating
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
    >
      New
    </Badge>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export type AdminChatsPanelProps = {
  onBack?: () => void;
};

type Analysis = {
  /** chat_reliability_analyses row id, once the server has created it */
  id: string | null;
  text: string;
  streaming: boolean;
  error: string | null;
};

type AnalysisRow = {
  id: string;
  status: "running" | "completed" | "failed";
  result: string;
  error: string | null;
  created_at: string;
};

// A run still marked "running" after this long died without saving (e.g. the
// server restarted mid-generation).
const STALE_ANALYSIS_MS = 15 * 60 * 1000;

export function AdminChatsPanel({ onBack }: AdminChatsPanelProps) {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">("all");
  const [selected, setSelected] = useState<SelectedChat | null>(null);
  // Analyses keyed by chat id. Runs keep going server-side when you switch
  // feedback, so each chat keeps its own entry instead of one shared slot.
  const [analyses, setAnalyses] = useState<Record<string, Analysis>>({});
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const analysesRef = useRef(analyses);
  analysesRef.current = analyses;
  const analysisReadersRef = useRef<Set<AbortController>>(new Set());
  const analysisPollsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());

  const [chatIdInput, setChatIdInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const loadFeedbacks = useCallback(async () => {
    setListLoading(true);
    setListError(null);

    // The edge function resolves submitter names and chat titles, which the
    // caller can't read directly through RLS.
    const { data, error: invokeError } = await supabase.functions.invoke(
      "get-admin-feedback-list",
      { body: { include_completed: true } }
    );

    setListLoading(false);

    const items = (data as { items?: AdminFeedbackListItem[] } | null)?.items;
    if (invokeError || !items) {
      console.error("[AdminChatsPanel] load feedbacks:", invokeError);
      setListError(invokeError?.message || "Failed to load feedback.");
      return;
    }

    setFeedbacks(
      items
        .filter((item) => item.target_type === "chat" || item.target_type === "project-chat")
        .map((item) => ({
          id: item.id,
          chat_id: item.target_type === "chat" ? item.target_id : null,
          project_chat_id: item.target_type === "project-chat" ? item.target_id : null,
          user_id: item.created_by.user_id,
          feedback: item.feedback,
          consent_to_read_chat: item.consent_to_read_chat,
          created_at: item.created_at,
          status: item.status,
          title: item.title,
          submitter_name: item.created_by.name,
        }))
    );
  }, []);

  useEffect(() => {
    void loadFeedbacks();
  }, [loadFeedbacks]);

  const filteredFeedbacks = useMemo(
    () =>
      statusFilter === "all"
        ? feedbacks
        : feedbacks.filter((f) => f.status === statusFilter),
    [feedbacks, statusFilter]
  );

  const statusCounts = useMemo(() => {
    const counts: Record<FeedbackStatus | "all", number> = {
      all: feedbacks.length,
      new: 0,
      investigating: 0,
      completed: 0,
    };
    for (const f of feedbacks) counts[f.status] += 1;
    return counts;
  }, [feedbacks]);

  // Core fetch — works for both direct ID lookup and feedback list selection
  const fetchChat = useCallback(
    async (
      chatId: string,
      knownFeedbackId?: string,
      knownStatus?: FeedbackStatus,
      targetKind?: FeedbackTargetKind
    ) => {
      const trimmed = chatId.trim();
      if (!trimmed) return;

      setReliabilityOpen(false);
      setSelected({
        chatId: trimmed,
        targetKind: targetKind ?? null,
        feedbackId: knownFeedbackId ?? null,
        feedbackStatus: knownStatus ?? "new",
        feedbackInfo: null,
        chatDetails: null,
        messages: [],
        accessLimited: false,
        loading: true,
        error: null,
      });

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const query = new URLSearchParams();
        query.set(targetKind === "project-chat" ? "project_chat_id" : "chat_id", trimmed);
        if (knownFeedbackId) query.set("feedback_id", knownFeedbackId);

        const url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-chat-messages?${query.toString()}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ""}`,
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          },
        });

        const json: AdminChatApiResponse = await res.json();

        if (!res.ok || !("success" in json)) {
          const msg =
            "error" in json ? json.error : "Failed to load chat messages.";
          setSelected((prev) =>
            prev ? { ...prev, loading: false, error: msg } : null
          );
          return;
        }

        // If we don't already know the feedback ID/status (direct lookup), fetch them
        let feedbackId = knownFeedbackId ?? null;
        let feedbackStatus: FeedbackStatus = knownStatus ?? "new";
        if (!feedbackId) {
          const { data: meta } = await supabase
            .from("chat_feedbacks")
            .select("id, status")
            .or(`chat_id.eq.${trimmed},project_chat_id.eq.${trimmed}`)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (meta) {
            feedbackId = meta.id as string;
            feedbackStatus = (meta.status as FeedbackStatus) ?? "new";
          }
        }

        setSelected({
          chatId: trimmed,
          targetKind: json.target_type,
          feedbackId,
          feedbackStatus,
          feedbackInfo: json.chat_feedback,
          chatDetails: json.chat_details,
          messages: json.messages,
          accessLimited: Boolean(json.access_limited),
          loading: false,
          error: null,
        });
      } catch (err) {
        setSelected((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : "Unexpected error.",
              }
            : null
        );
      }
    },
    []
  );

  const handleSelectFeedback = useCallback(
    (feedback: FeedbackItem) => {
      const targetId = getFeedbackTargetId(feedback);
      if (!targetId) return;
      setChatIdInput(targetId);
      void fetchChat(
        targetId,
        feedback.id,
        feedback.status,
        getFeedbackTargetKind(feedback)
      );
    },
    [fetchChat]
  );

  const handleLookup = useCallback(() => {
    void fetchChat(chatIdInput);
  }, [fetchChat, chatIdInput]);

  const handleStatusChange = useCallback(
    async (feedbackId: string, newStatus: FeedbackStatus) => {
      const { error } = await supabase
        .from("chat_feedbacks")
        .update({ status: newStatus })
        .eq("id", feedbackId);

      if (error) return;

      setFeedbacks((prev) =>
        prev.map((f) => (f.id === feedbackId ? { ...f, status: newStatus } : f))
      );
      setSelected((prev) =>
        prev ? { ...prev, feedbackStatus: newStatus } : null
      );
    },
    []
  );

  const patchAnalysis = useCallback((chatId: string, patch: Partial<Analysis>) => {
    setAnalyses((prev) => {
      const current = prev[chatId] ?? { id: null, text: "", streaming: false, error: null };
      return { ...prev, [chatId]: { ...current, ...patch } };
    });
  }, []);

  // Read a text stream into the chat's entry. Not aborted when the selection
  // changes — only on unmount — so switching back shows the live output.
  const readAnalysisStream = useCallback(
    async (chatId: string, body: ReadableStream<Uint8Array>, controller: AbortController) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      patchAnalysis(chatId, { text: "", streaming: true, error: null });
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          setAnalyses((prev) => {
            const current = prev[chatId];
            return current ? { ...prev, [chatId]: { ...current, text: current.text + chunk } } : prev;
          });
        }
        patchAnalysis(chatId, { streaming: false });
      } catch (err) {
        if (controller.signal.aborted) return;
        patchAnalysis(chatId, {
          streaming: false,
          error: err instanceof Error ? err.message : "Analysis failed.",
        });
      } finally {
        analysisReadersRef.current.delete(controller);
      }
    },
    [patchAnalysis]
  );

  // Poll the saved row until a run that couldn't be resumed live finishes.
  const pollAnalysis = useCallback(
    (chatId: string, analysisId: string) => {
      const stop = () => {
        clearInterval(timer);
        analysisPollsRef.current.delete(timer);
      };
      const timer = setInterval(async () => {
        const { data } = await supabase
          .from("chat_reliability_analyses")
          .select("id, status, result, error, created_at")
          .eq("id", analysisId)
          .maybeSingle();
        const row = data as AnalysisRow | null;
        if (!row || row.status === "running") {
          if (row && Date.now() - new Date(row.created_at).getTime() > STALE_ANALYSIS_MS) {
            stop();
            patchAnalysis(chatId, { streaming: false, error: "This analysis didn't finish. Re-run it." });
          }
          return;
        }
        stop();
        patchAnalysis(chatId, {
          text: row.result,
          streaming: false,
          error: row.status === "failed" ? row.error ?? "Analysis failed." : null,
        });
      }, 3000);
      analysisPollsRef.current.add(timer);
    },
    [patchAnalysis]
  );

  const authHeader = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return `Bearer ${session?.access_token ?? ""}`;
  }, []);

  // Load the latest saved analysis for a chat, reattaching if it's running.
  const loadLatestAnalysis = useCallback(
    async (chatId: string, targetKind: FeedbackTargetKind | null) => {
      if (analysesRef.current[chatId]) return;
      const { data, error } = await supabase
        .from("chat_reliability_analyses")
        .select("id, status, result, error, created_at")
        .eq(targetKind === "project-chat" ? "project_chat_id" : "chat_id", chatId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error("[AdminChatsPanel] load analysis:", error);
        return;
      }
      const row = data as AnalysisRow | null;
      if (!row || analysesRef.current[chatId]) return;

      if (row.status !== "running") {
        patchAnalysis(chatId, {
          id: row.id,
          text: row.result,
          streaming: false,
          error: row.status === "failed" ? row.error ?? "Analysis failed." : null,
        });
        return;
      }
      if (Date.now() - new Date(row.created_at).getTime() > STALE_ANALYSIS_MS) {
        patchAnalysis(chatId, { id: row.id, streaming: false, error: "This analysis didn't finish. Re-run it." });
        return;
      }

      patchAnalysis(chatId, { id: row.id, text: "", streaming: true, error: null });
      const controller = new AbortController();
      analysisReadersRef.current.add(controller);
      try {
        const res = await fetch(`/api/analyse-chat-reliability/${row.id}/stream`, {
          headers: { Authorization: await authHeader() },
          signal: controller.signal,
        });
        if (res.status === 200 && res.body) {
          await readAnalysisStream(chatId, res.body, controller);
          return;
        }
      } catch {
        // Fall through to polling.
      }
      analysisReadersRef.current.delete(controller);
      pollAnalysis(chatId, row.id);
    },
    [authHeader, patchAnalysis, pollAnalysis, readAnalysisStream]
  );

  useEffect(() => {
    if (!selected || selected.loading) return;
    void loadLatestAnalysis(selected.chatId, selected.targetKind);
  }, [selected, loadLatestAnalysis]);

  useEffect(() => {
    const readers = analysisReadersRef.current;
    const polls = analysisPollsRef.current;
    return () => {
      for (const controller of readers) controller.abort();
      for (const timer of polls) clearInterval(timer);
    };
  }, []);

  const handleReliability = useCallback(async () => {
    if (!selected || selected.loading) return;
    const chatId = selected.chatId;
    if (analysesRef.current[chatId]?.streaming) {
      setReliabilityOpen(true);
      return;
    }

    patchAnalysis(chatId, { id: null, text: "", streaming: true, error: null });
    setReliabilityOpen(true);

    const controller = new AbortController();
    analysisReadersRef.current.add(controller);
    try {
      const res = await fetch("/api/analyse-chat-reliability", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: await authHeader(),
        },
        body: JSON.stringify({
          chatId,
          feedbackId: selected.feedbackId ?? undefined,
          targetKind: selected.targetKind ?? undefined,
          consentGiven: selected.feedbackInfo?.consent_to_read_chat ?? false,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `Request failed (${res.status})`);
      }

      patchAnalysis(chatId, { id: res.headers.get("X-Analysis-Id") });
      await readAnalysisStream(chatId, res.body, controller);
    } catch (err) {
      analysisReadersRef.current.delete(controller);
      if (controller.signal.aborted) return;
      patchAnalysis(chatId, {
        streaming: false,
        error: err instanceof Error ? err.message : "Analysis failed.",
      });
    }
  }, [selected, authHeader, patchAnalysis, readAnalysisStream]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleLookup();
    },
    [handleLookup]
  );

  const feedbackSummary = useCallback(
    (f: FeedbackItem) => (
      <div className="min-w-0 space-y-1">
        <p className="line-clamp-2">{f.feedback}</p>
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <MessageSquareIcon className="size-3 shrink-0" />
          <span className="truncate">
            {f.title || `${getFeedbackTargetId(f)?.slice(0, 8)}…`}
          </span>
          {f.project_chat_id ? (
            <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
              Project
            </Badge>
          ) : null}
          {f.consent_to_read_chat ? null : (
            <span className="flex shrink-0 items-center gap-1">
              · <EyeOffIcon className="size-3" /> No consent
            </span>
          )}
        </p>
      </div>
    ),
    []
  );

  // With the detail card open the list only gets 2/5 of the width, so the
  // columns collapse into one stacked cell to avoid horizontal scrolling.
  const compact = selected !== null;
  const columns = useMemo<DataTableColumn<FeedbackItem>[]>(
    () =>
      compact
        ? [
            {
              header: "Feedback",
              render: (f) => (
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {f.submitter_name ?? "Unknown user"}
                      </span>
                      {" · "}
                      {formatFeedbackDate(f.created_at)}
                    </span>
                    <StatusBadge status={f.status ?? "new"} />
                  </div>
                  {feedbackSummary(f)}
                </div>
              ),
              sortKey: "created_at",
              sortValue: (f) => f.created_at,
            },
          ]
        : [
            {
              header: "Submitted",
              width: "w-[150px]",
              render: (f) => formatFeedbackDate(f.created_at),
              sortKey: "created_at",
              sortValue: (f) => f.created_at,
            },
            {
              header: "User",
              width: "w-[140px]",
              render: (f) => f.submitter_name ?? "Unknown user",
              sortKey: "user",
              sortValue: (f) => f.submitter_name ?? "",
            },
            {
              header: "Feedback",
              render: feedbackSummary,
            },
            {
              header: "Status",
              width: "w-[120px]",
              render: (f) => <StatusBadge status={f.status ?? "new"} />,
              sortKey: "status",
              sortValue: (f) => f.status,
            },
          ],
    [compact, feedbackSummary]
  );

  return (
    <div
      className={cn(
        "mx-auto flex h-full w-full flex-col overflow-hidden",
        selected ? "max-w-7xl" : "max-w-3xl"
      )}
    >
      <PageHeader
        title="Chat Feedback"
        description="Inspect chats shared by users as part of their feedback submissions."
        onBack={onBack}
        showBack={Boolean(onBack)}
        actions={
          <InputGroup className="sm:w-72">
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
            <InputGroupInput
              ref={inputRef}
              placeholder="Open a chat by ID…"
              value={chatIdInput}
              onChange={(e) => setChatIdInput(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Chat ID"
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                onClick={handleLookup}
                disabled={!chatIdInput.trim()}
              >
                Open
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        }
      />
      <div
        className={cn(
          "min-h-0 flex-1 px-4 py-5 sm:px-6",
          // With a chat open, each column scrolls on its own instead of the page.
          selected ? "overflow-hidden" : "overflow-y-auto"
        )}
      >
        <div className={selected ? "flex h-full gap-6" : "w-full"}>
          <div
            className={cn(
              "min-w-0",
              selected && "hidden min-h-0 w-2/5 shrink-0 overflow-y-auto sm:block"
            )}
          >
            {listError ? (
              <p className="text-sm text-destructive">{listError}</p>
            ) : (
              <DataTable
                columns={columns}
                data={filteredFeedbacks}
                getRowKey={(f) => f.id}
                onRowClick={handleSelectFeedback}
                selectedKeys={selected?.feedbackId ? new Set([selected.feedbackId]) : undefined}
                loading={listLoading}
                onRefresh={() => void loadFeedbacks()}
                searchFilter={(f, query) =>
                  f.feedback.toLowerCase().includes(query) ||
                  (getFeedbackTargetId(f) ?? "").toLowerCase().includes(query) ||
                  (f.title ?? "").toLowerCase().includes(query) ||
                  (f.submitter_name ?? "").toLowerCase().includes(query)
                }
                searchPlaceholder="Search feedback…"
                searchActions={
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as FeedbackStatus | "all")}
                  >
                    <SelectTrigger className="h-9 w-44 text-sm" aria-label="Filter by status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_FILTERS.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>
                          {label} ({statusCounts[value]})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
                emptyMessage="No chat feedback found."
                itemLabel="entry"
                itemLabelPlural="entries"
                wrapCells
              />
            )}
          </div>
          {selected && (
            <section
              className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card"
              aria-label="Feedback details"
            >
              <ChatViewer
                selected={selected}
                reliability={analyses[selected.chatId] ?? null}
                reliabilityOpen={reliabilityOpen}
                onReliabilityOpenChange={setReliabilityOpen}
                onOpenReliability={() => setReliabilityOpen(true)}
                onReliability={handleReliability}
                onStatusChange={handleStatusChange}
                onClose={() => setSelected(null)}
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Admin message parts renderer ────────────────────────────────────────────

function AdminMessageParts({ msg }: { msg: AdminMessage }) {
  const [reasoningOpen, setReasoningOpen] = useState<Record<number, boolean>>(
    {}
  );

  const parts: MessagePart[] =
    msg.parts && msg.parts.length > 0
      ? msg.parts
      : [{ type: "text", text: msg.content }];

  return (
    <div className="flex flex-col gap-2">
      {parts.map((part, i) => {
        if (part.type === "text" && part.text) {
          return (
            <MessageResponse key={i} isAnimating={false}>
              {part.text}
            </MessageResponse>
          );
        }

        if (part.type === "reasoning" && part.text) {
          const open = reasoningOpen[i] ?? false;
          return (
            <div key={i} className="rounded-md border border-border bg-muted/40">
              <button
                type="button"
                onClick={() =>
                  setReasoningOpen((prev) => ({ ...prev, [i]: !open }))
                }
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="flex-1">Thinking</span>
                <span>{open ? "▲" : "▼"}</span>
              </button>
              {open && (
                <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  {part.text}
                </div>
              )}
            </div>
          );
        }

        // Tool invocations (tool-* or dynamic-tool)
        if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
          const toolName = part.type === "dynamic-tool"
            ? String((part as MessagePart).toolName ?? "tool")
            : part.type.replace(/^tool-/, "");
          const state = String((part as MessagePart).state ?? "");
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5"
            >
              <Badge variant="outline" className="text-[10px] shrink-0">
                {toolName}
              </Badge>
              {state && (
                <span className="text-[10px] text-muted-foreground">{state}</span>
              )}
            </div>
          );
        }

        // File / image parts
        if (part.type === "file") {
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5"
            >
              <Badge variant="outline" className="text-[10px]">file</Badge>
              <span className="text-[10px] text-muted-foreground truncate">
                {String((part as MessagePart).name ?? (part as MessagePart).mediaType ?? "attachment")}
              </span>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ─── Chat viewer ──────────────────────────────────────────────────────────────

function ChatViewer({
  selected,
  reliability,
  reliabilityOpen,
  onReliabilityOpenChange,
  onOpenReliability,
  onReliability,
  onStatusChange,
  onClose,
}: {
  selected: SelectedChat;
  reliability: Analysis | null;
  reliabilityOpen: boolean;
  onReliabilityOpenChange: (open: boolean) => void;
  onOpenReliability: () => void;
  onReliability: () => void;
  onStatusChange: (feedbackId: string, status: FeedbackStatus) => Promise<void>;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [sheetWidth, setSheetWidth] = useState(640);
  const isDragging = useRef(false);

  const handleResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const newWidth = window.innerWidth - e.clientX;
    setSheetWidth(Math.min(Math.max(newWidth, 360), window.innerWidth - 80));
  }, []);

  const handleResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  const handleCopy = useCallback(() => {
    if (!reliability?.text) return;
    void navigator.clipboard.writeText(reliability.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [reliability]);

  const handleDownload = useCallback(() => {
    if (!reliability?.text) return;
    const blob = new Blob([reliability.text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reliability-${selected.chatId.slice(0, 8)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [reliability, selected.chatId]);
  const { feedbackInfo, chatDetails, messages, accessLimited, loading, error } =
    selected;

  const closeButton = (
    <Button type="button" variant="outline" size="sm" onClick={onClose}>
      Close
    </Button>
  );

  if (loading) {
    return (
      <div className="flex flex-1 flex-col">
        <div className="flex justify-end p-4">{closeButton}</div>
        <ListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col">
        <div className="flex justify-end p-4">{closeButton}</div>
        <div className="flex flex-1 items-center justify-center p-6">
          <p className="max-w-sm text-center text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  const enabledFeatures = chatDetails?.active_tool_names ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Header: title + context, then actions, feedback and chat details */}
      <div className="shrink-0 space-y-4 border-b border-border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-foreground">
              {chatDetails?.name ?? selected.chatId}
            </h2>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {[
                selected.targetKind === "project-chat" ? "Project chat" : "Chat",
                chatDetails?.model,
                chatDetails?.agent_name ? `Agent: ${chatDetails.agent_name}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {selected.feedbackId && (
              <Select
                value={selected.feedbackStatus}
                onValueChange={(v) =>
                  void onStatusChange(selected.feedbackId!, v as FeedbackStatus)
                }
              >
                <SelectTrigger className="h-8 w-36 text-xs" aria-label="Feedback status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={reliability ? onOpenReliability : onReliability}
                  aria-label={
                    reliability?.streaming
                      ? "Analysing chat"
                      : reliability
                        ? "View analysis"
                        : "Analyse chat"
                  }
                  className={cn(reliability && !reliability.streaming && "bg-accent text-accent-foreground")}
                >
                  {reliability?.streaming ? (
                    <Spinner className="size-3.5" />
                  ) : (
                    <SparklesIcon size={14} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {reliability?.streaming
                  ? "Analysing… (keeps running if you leave)"
                  : reliability
                    ? "View analysis"
                    : "Analyse chat"}
              </TooltipContent>
            </Tooltip>
            {closeButton}
          </div>
        </div>

        {feedbackInfo?.feedback && (
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Feedback
            </p>
            <blockquote className="max-h-40 overflow-y-auto rounded-md border-l-4 border-brand bg-brand/10 px-3 py-2.5 text-sm whitespace-pre-wrap text-foreground">
              {feedbackInfo.feedback}
            </blockquote>
            <p className="text-xs text-muted-foreground">
              {feedbackInfo.submitter_name ? (
                <>
                  <span className="font-medium text-foreground">
                    {feedbackInfo.submitter_name}
                  </span>
                  {" · "}
                </>
              ) : null}
              {new Intl.DateTimeFormat("en-NZ", {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "Pacific/Auckland",
              }).format(new Date(feedbackInfo.created_at))}{" "}
              NZT
            </p>
          </div>
        )}

        {enabledFeatures.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Tools enabled
            </p>
            <div className="flex flex-wrap gap-1.5">
              {enabledFeatures.map((feat) => (
                <Badge
                  key={feat}
                  variant="secondary"
                  className="bg-activated/15 text-xs text-activated"
                >
                  {feat}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {accessLimited && (
          <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            User did not consent to chat review. Showing feedback note only.
          </p>
        )}
      </div>

      {/* Body: messages + analysis side by side */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Message list */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No messages found.
            </p>
          ) : (
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-4 py-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Conversation · {messages.length} {messages.length === 1 ? "message" : "messages"}
              </p>
              {messages.map((msg) => (
                <Message key={msg.id} from={msg.role as "user" | "assistant"}>
                  <MessageContent>
                    <AdminMessageParts msg={msg} />
                  </MessageContent>
                </Message>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reliability analysis — wider Sheet */}
      <Sheet open={reliabilityOpen} onOpenChange={onReliabilityOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col gap-0 p-0 transition-none"
          style={{ width: sheetWidth, maxWidth: "none" }}
        >
          {/* Drag-to-resize handle */}
          <div
            className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
          <SheetHeader className="shrink-0 border-b border-border px-6 pt-4 pb-3">
            <div className="flex items-center gap-2 pr-8">
              <SparklesIcon size={16} className="shrink-0 text-muted-foreground" />
              <SheetTitle className="text-sm">Chat Analysis</SheetTitle>
            </div>
            <SheetDescription className="text-xs">
              {selected.feedbackInfo?.consent_to_read_chat
                ? `Full content analysis — chat ${selected.chatId.slice(0, 8)}… (user consented)`
                : `Structural analysis — chat ${selected.chatId.slice(0, 8)}… (no message content)`}
            </SheetDescription>
            <div className="flex items-center gap-1.5 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!reliability?.text || reliability.streaming}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={!reliability?.text || reliability.streaming}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <DownloadIcon size={12} />
                Download .md
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onReliability}
                disabled={reliability?.streaming ?? false}
                className="h-7 gap-1.5 px-2.5 text-xs"
              >
                <RefreshCcwIcon size={12} />
                Re-run
              </Button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
            {reliability?.error ? (
              <p className="text-sm text-destructive">{reliability.error}</p>
            ) : reliability ? (
              <MessageResponse isAnimating={reliability.streaming}>
                {reliability.text}
              </MessageResponse>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
